import { useState, useEffect } from "react";
import PageSeo from "@/components/seo/PageSeo";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { API } from "@/App";
import {
  Zap, Target, Shield, BarChart3, Eye, TrendingUp, TrendingDown,
  Clock, ArrowRight, ChevronRight, Users, Brain, Activity,
  Crosshair, Layers, Search, CheckCircle, AlertTriangle, Star,
  ChevronDown, ChevronUp, PenLine, FileText, Check
} from "lucide-react";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.6, delay }} className={className}>
    {children}
  </motion.div>
);

const DrishtiLandingPage = () => {
  const [openFaq, setOpenFaq] = useState(null);
  const [sc, setSc] = useState({});

  useEffect(() => {
    axios.get(`${API}/site-config`)
      .then(res => { if (res.data) setSc(res.data); })
      .catch(() => {});
  }, []);

  const features = [
    { icon: TrendingUp, title: "Demand Zone Detection", desc: "Track stocks near strong demand areas where buying interest may emerge.", color: "#00FF88" },
    { icon: TrendingDown, title: "Supply Zone Detection", desc: "Identify supply zones where price may react, reject, or reverse.", color: "#FF3B30" },
    { icon: Eye, title: "Approaching Zone Alerts", desc: "Know which stocks are moving close to a demand or supply zone before they enter it.", color: "#00F0FF" },
    { icon: Crosshair, title: "In-Zone Tracking", desc: "Quickly see which stocks are already inside important zones and ready for setup confirmation.", color: "#FFCC00" },
    { icon: Zap, title: "MIT / QIT / DIT / HYIT Setups", desc: "Structured setup classification for better trade filtering and clarity.", color: "#A78BFA" },
    { icon: Target, title: "Entry, SL & Target Guidance", desc: "Every valid setup is presented with action-oriented details.", color: "#00FF88" },
    { icon: Clock, title: "Multi-Timeframe Analysis", desc: "Hourly, Daily, Weekly, Monthly, and Quarterly zone tracking.", color: "#00F0FF" },
    { icon: Brain, title: "Institutional-Style Logic", desc: "Designed to support traders who want to trade with process, not emotions.", color: "#FFCC00" }
  ];

  const steps = [
    { num: "01", title: sc.drishti_step_1_title || "Market Scans Zones", desc: sc.drishti_step_1_desc || "Drishti identifies demand and supply zones across selected stocks and indices." },
    { num: "02", title: sc.drishti_step_2_title || "Tracks Price Position", desc: sc.drishti_step_2_desc || "It shows whether stocks are approaching zones or already trading inside zones." },
    { num: "03", title: sc.drishti_step_3_title || "Waits for Setup Confirmation", desc: sc.drishti_step_3_desc || "The system filters trades using setup logic such as MIT, QIT, DIT, and HYIT." },
    { num: "04", title: sc.drishti_step_4_title || "Sends Structured Alerts", desc: sc.drishti_step_4_desc || "Once conditions align, Drishti provides a trade alert with entry, stop loss, target, and setup type." }
  ];

  const setups = [
    { name: "MIT", desc: sc.drishti_mit_desc || "A structured setup type triggered when price behavior aligns with MIT conditions near key zones.", color: "#00FF88" },
    { name: "QIT", desc: sc.drishti_qit_desc || "Used when the market forms a qualifying setup based on QIT logic and confirmation criteria.", color: "#00F0FF" },
    { name: "DIT", desc: sc.drishti_dit_desc || "Designed for setups that match defined DIT conditions near key zones.", color: "#FFCC00" },
    { name: "HYIT", desc: sc.drishti_hyit_desc || "For high-quality opportunities where multiple supporting conditions align.", color: "#A78BFA" }
  ];

  const forWho = [
    { icon: Users, title: "Beginner Traders", desc: "Who want more structure and less confusion." },
    { icon: BarChart3, title: "Intermediate Traders", desc: "Who understand charts but struggle with timing and entries." },
    { icon: Clock, title: "Busy Traders", desc: "Who cannot scan multiple stocks manually all day." },
    { icon: Star, title: "Jamadhan Students", desc: "Who want an AI-assisted layer to support their market analysis." }
  ];

  const faqs = [
    { q: "What is Drishti AI?", a: "Drishti AI is an AI-assisted trading alert system that helps users track demand zones, supply zones, and structured trade setups." },
    { q: "Does Drishti give entry and stop loss?", a: "Yes, valid alerts are shown with entry, stop loss, and target guidance." },
    { q: "What are MIT, QIT, DIT, and HYIT?", a: "These are structured trade setup categories used within the Drishti system for better trade filtering." },
    { q: "Is Drishti only for experienced traders?", a: "No. The tool is designed to make the market more structured and readable for both learners and active traders." },
    { q: "Is Drishti part of Jamadhan courses?", a: "It can be offered as part of selected programs, premium access, or separate subscription plans." }
  ];

  const problems = [
    "Enter late and miss the real move",
    "Chase candles without any zone awareness",
    "Ignore demand and supply structures",
    "Trade without defined stop loss",
    "Miss strong institutional-grade setups"
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden" data-testid="drishti-landing">
      <PageSeo
        path="/drishti-ai"
        fallbackTitle={sc.drishti_seo_title || (sc.drishti_hero_heading ? `${sc.drishti_hero_heading} | Drishti AI` : "Drishti AI — Real-Time Institutional Trading Signals & Demand Supply Zones")}
        fallbackDescription={sc.drishti_seo_description || sc.drishti_hero_description || "AI-powered demand and supply zone alerts for smarter trading decisions."}
      />
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/[0.04]">
        <div className="max-w-6xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded overflow-hidden shadow-[0_0_20px_rgba(0,255,136,0.3)]">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-lg font-bold text-white font-outfit tracking-tight">DRISHTI AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/drishti"><Button style={{ backgroundColor: "#00FF88", color: "#000" }} className="font-bold hover:brightness-110 rounded-sm px-6 text-sm border-0 shadow-none" data-testid="hero-cta">Get 3 Months Free</Button></Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      {/* Hero */}
      <section className="pt-32 pb-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(0,240,255,0.05),transparent_60%)]" />

        <div className="max-w-7xl mx-auto relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">

          {/* Left Text */}
          <Reveal className="text-left">
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase mb-4">
              {sc.drishti_hero_tagline || "JAMADHAN DRISHTI AI"}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-[76px] font-black font-outfit leading-[1.05] mb-6">
              {sc.drishti_hero_heading || "Trade with Institutional Vision"}
            </h1>
            <p className="text-base text-[#94A3B8] font-plex max-w-xl mb-8">
              {sc.drishti_hero_description || "Drishti AI helps traders identify demand and supply zones, track stocks approaching key levels, and receive structured alerts with entry, stop loss, and target guidance."}
            </p>

            <div className="flex items-center gap-3 flex-wrap mb-10">
              <Link to="/drishti">
                <Button style={{ backgroundColor: "#00FF88", color: "#000" }} className="font-black hover:brightness-110 rounded-sm px-8 py-3 text-sm h-auto border-0 shadow-[0_0_30px_rgba(0,255,136,0.3)]">
                  Start 3 Months Free Trial (Pay ₹1)
                </Button>
              </Link>
              <a href="#plans">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-sm px-8 py-3 text-sm h-auto font-bold">
                  View Access Options
                </Button>
              </a>
            </div>

            <div className="flex items-center gap-6 flex-wrap text-xs font-jetbrains text-[#94A3B8]">
              {["AI-Powered Zone Alerts", "MIT | QIT | DIT | HYIT", "Entry, SL & Target", "Multi-Timeframe"].map(t => (
                <span key={t} className="flex items-center gap-1">
                  <CheckCircle className="w-3 h-3 text-[#00FF88]" />{t}
                </span>
              ))}
            </div>
          </Reveal>

          {/* Right Animation */}
          <Reveal delay={0.2} className="relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[640px]">

              {/* Glowing Portal Behind the Video */}
              {/* This bright orb ensures the dark head is visible, while mix-blend-multiply hides the white background on the edges */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] sm:w-[500px] h-[350px] sm:h-[500px] bg-[#E2E8F0] blur-[80px] rounded-full pointer-events-none" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] sm:w-[300px] h-[200px] sm:h-[300px] bg-[#00F0FF]/30 blur-[60px] rounded-full pointer-events-none" />

              {/* Video wrapper with continuous floating animation and radial mask */}
              <motion.div
                animate={{ y: [0, -15, 0] }}
                transition={{ duration: 6, ease: "easeInOut", repeat: Infinity }}
                className="relative z-10 w-full aspect-[640/594]"
                style={{
                  maskImage: 'radial-gradient(ellipse at center, black 65%, transparent 100%)',
                  WebkitMaskImage: 'radial-gradient(ellipse at center, black 65%, transparent 100%)'
                }}
              >
                {/* 
                  Using mix-blend-multiply makes the white background of the video disappear against the black UI.
                  The bright orb behind the video makes the dark head and blue glowing wires visible.
                  This preserves the exact look of SS2 on a dark theme!
                */}
                <video
                  src="/drishti-head-hero.mp4"
                  preload="auto"
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="w-full h-full object-cover mix-blend-multiply opacity-100"
                />
              </motion.div>

            </div>
          </Reveal>

        </div>
      </section>

      {/* What is Drishti */}
      <section className="py-20 px-4 border-t border-white/[0.04]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#00FF88] uppercase mb-3">ABOUT</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit mb-6">What is Jamadhan Drishti AI?</h2>
            <div className="space-y-4 text-base text-[#CBD5E1] font-plex leading-relaxed">
              <p>Jamadhan Drishti AI is an AI-assisted trading alert system built to help traders spot high-potential setups before the move happens. Instead of random tips or noisy signals, Drishti focuses on structured market opportunities based on demand zones, supply zones, price behavior, and institutional-style setup logic.</p>
              <p>The platform helps traders monitor stocks approaching important zones, identify stocks already trading inside zones, and act only when specific trade conditions such as MIT, QIT, DIT, or HYIT are triggered.</p>
              <p className="text-[#00FF88] font-semibold">In simple words, Drishti helps you move from confusion to clarity.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 px-4 bg-[#0D0D0D] border-t border-white/[0.04]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#FF3B30] uppercase mb-3">THE PROBLEM</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit mb-3">Most Traders Enter Too Late or Without Structure</h2>
            <p className="text-lg text-[#94A3B8] font-plex mb-8">The problem is not lack of charts. <span className="text-white font-semibold">The problem is lack of clarity.</span></p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {problems.map((p, i) => (
              <Reveal key={p} delay={i * 0.08}>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.04]">
                  <AlertTriangle className="w-5 h-5 text-[#FF3B30] shrink-0" />
                  <p className="text-sm text-[#CBD5E1] font-plex">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.4}>
            <div className="mt-8 p-5 rounded-lg border border-[#00FF88]/20 bg-[#00FF88]/[0.03]">
              <p className="text-base text-[#00FF88] font-semibold font-outfit">Drishti is built to solve this.</p>
              <p className="text-sm text-[#CBD5E1] font-plex mt-1">It brings structure by showing where price is relative to key zones and when a valid setup is forming.</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-20 px-4 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase mb-3 text-center">PROCESS</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-center mb-12">How Drishti AI Works</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {steps.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.12}>
                <div className="text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#00F0FF]/20 to-[#00FF88]/20 flex items-center justify-center mx-auto mb-4">
                    <span className="text-xl font-black font-jetbrains text-[#00F0FF]">{s.num}</span>
                  </div>
                  <h3 className="text-base font-bold font-outfit text-white mb-2">{s.title}</h3>
                  <p className="text-sm text-[#94A3B8] font-plex leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 px-4 bg-[#0D0D0D] border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#FFCC00] uppercase mb-3 text-center">FEATURES</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-center mb-12">Built for Structured Trading Decisions</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {features.map((f, i) => (
              <Reveal key={f.title} delay={i * 0.06}>
                <div className="terminal-card rounded-lg p-5 h-full hover:border-white/10 transition-colors">
                  <f.icon className="w-6 h-6 mb-3" style={{ color: f.color }} />
                  <h3 className="text-sm font-semibold font-outfit text-white mb-2">{f.title}</h3>
                  <p className="text-xs text-[#94A3B8] font-plex leading-relaxed">{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Setup Types */}
      <section className="py-20 px-4 border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#A78BFA] uppercase mb-3 text-center">TRADE SETUPS</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-center mb-4">Multiple Trade Setup Types</h2>
            <p className="text-base text-[#94A3B8] font-plex text-center max-w-xl mx-auto mb-12">Drishti categorizes opportunities into structured trade types so traders understand not just where price is, but what kind of setup is developing.</p>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {setups.map((s, i) => (
              <Reveal key={s.name} delay={i * 0.1}>
                <div className="terminal-card rounded-lg p-6 text-center hover:border-white/10 transition-colors" style={{ borderTop: `3px solid ${s.color}` }}>
                  <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: `${s.color}15` }}>
                    <span className="text-lg font-black font-jetbrains" style={{ color: s.color }}>{s.name}</span>
                  </div>
                  <p className="text-xs text-[#94A3B8] font-plex leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Alerts */}
      <section className="py-20 px-4 bg-[#0D0D0D] border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#00FF88] uppercase mb-3 text-center">ALERTS</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-center mb-12">Structured Alerts, Not Random Noise</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { type: "Zone Approaching", color: "#00F0FF", items: ["Stock: RELIANCE", "Zone Type: Demand Zone", "Timeframe: 15 Min", "Current Price: 2,482", "Zone Entry: 2,450 - 2,460", "Distance: 1.3%", "Status: Prepare for MIT confirmation"] },
              { type: "Trade Activated", color: "#00FF88", items: ["Stock: RELIANCE", "Setup: MIT", "Timeframe: 5 Min", "Entry: 2,462", "Stop Loss: 2,448", "Target 1: 2,485 | Target 2: 2,510", "Risk Reward: 1:3", "Confidence: High"] },
              { type: "Update", color: "#FFCC00", items: ["Stock: RELIANCE", "Target 1 Hit", "Action: Trail stop loss to cost", "Profit Locked: Partial booking"] },
              { type: "Warning", color: "#FF3B30", items: ["Demand Zone Broken", "Avoid Long Bias", "Look for next valid reaction", "Setup Invalidated"] }
            ].map((ex, i) => (
              <Reveal key={ex.type} delay={i * 0.1}>
                <div className="terminal-card rounded-lg p-5" style={{ borderLeft: `3px solid ${ex.color}` }}>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-2 h-2 rounded-full" style={{ background: ex.color }} />
                    <p className="text-xs font-jetbrains font-bold uppercase tracking-wider" style={{ color: ex.color }}>{ex.type}</p>
                  </div>
                  <div className="space-y-1.5">
                    {ex.items.map(item => (
                      <p key={item} className="text-xs text-[#CBD5E1] font-plex">{item}</p>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who it's for */}
      <section className="py-20 px-4 border-t border-white/[0.04]">
        <div className="max-w-4xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#00F0FF] uppercase mb-3 text-center">AUDIENCE</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-center mb-12">Who Should Use Drishti AI?</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {forWho.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1}>
                <div className="terminal-card rounded-lg p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-[#00F0FF]/10 flex items-center justify-center shrink-0">
                    <w.icon className="w-5 h-5 text-[#00F0FF]" />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold font-outfit text-white mb-1">{w.title}</h3>
                    <p className="text-xs text-[#94A3B8] font-plex">{w.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 px-4 bg-[#0D0D0D] border-t border-white/[0.04]">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="font-jetbrains text-[10px] tracking-[0.3em] text-[#FFCC00] uppercase mb-3 text-center">FAQ</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-outfit text-center mb-10">Frequently Asked Questions</h2>
          </Reveal>
          <div className="space-y-2">
            {faqs.map((faq, i) => (
              <Reveal key={faq.q} delay={i * 0.05}>
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full text-left terminal-card rounded-lg p-4 hover:border-white/10 transition-colors">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold font-outfit text-white pr-4">{faq.q}</p>
                    {openFaq === i ? <ChevronUp className="w-4 h-4 text-[#94A3B8] shrink-0" /> : <ChevronDown className="w-4 h-4 text-[#94A3B8] shrink-0" />}
                  </div>
                  {openFaq === i && <p className="text-sm text-[#CBD5E1] font-plex mt-3 leading-relaxed">{faq.a}</p>}
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 sm:px-12 lg:px-20 border-t border-white/[0.04] relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,rgba(0,255,136,0.05),transparent_60%)]" />
        <div className="max-w-[1280px] mx-auto w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">

            {/* Left Column */}
            <div className="lg:col-span-5 flex flex-col justify-center items-start text-left lg:pr-6">
              <Reveal>
                <h2 className="text-4xl sm:text-5xl lg:text-[60px] leading-[1.08] tracking-tight font-black font-outfit mb-6">
                  Start Seeing the Market with More <span className="text-[#00FF88]">Clarity</span>
                </h2>
                <p className="text-lg text-[#CBD5E1] font-plex mb-8 max-w-[480px] leading-relaxed">
                  Whether you are learning trading or already active in the market, Drishti helps you focus on structured opportunities instead of random entries.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <Link to="/login">
                    <Button style={{ backgroundColor: "#00FF88", color: "#000" }} className="font-bold hover:brightness-110 rounded-[16px] px-8 py-3 text-sm h-auto border-0 shadow-lg transition-all">
                      Request Demo
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 rounded-[16px] px-8 py-3 text-sm h-auto transition-all">
                      Join Jamadhan
                    </Button>
                  </Link>
                </div>
              </Reveal>
            </div>

            {/* Right Column: Hero Robot Companion video & Floating Tags */}
            <div className="lg:col-span-7 relative w-full flex items-center justify-center lg:justify-end py-10 pointer-events-none">

              {/* Decorative Orbit Art */}
              <div className="absolute top-[30%] left-[20%] w-[280px] sm:w-[420px] h-[280px] sm:h-[420px] bg-sky-400/15 rounded-full blur-[90px] sm:blur-[110px] -z-10 animate-[pulse_7s_infinite]" />
              <div className="absolute w-[360px] sm:w-[620px] h-[360px] sm:h-[620px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-[52%] -z-10 opacity-35">
                <div className="w-full h-full rounded-full border-[1px] border-sky-400/30 border-dashed" />
              </div>
              <div className="absolute w-[240px] sm:w-[420px] h-[240px] sm:h-[420px] top-1/2 left-1/2 -translate-x-1/2 -translate-y-[52%] -z-10 opacity-20">
                <div className="w-full h-full rounded-full border-[1px] border-sky-400/40" />
              </div>

              {/* Centered Robot Companion Video (Premium Glass Card Wrapper) */}
              <div className="relative max-w-[500px] w-full flex justify-center z-10 mx-auto mt-8 lg:mt-0">
                {/* Ambient glow behind the card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[110%] bg-white/5 blur-[80px] rounded-full pointer-events-none" />
                
                {/* Premium Glass Frame */}
                <div className="relative p-2 sm:p-3 rounded-[32px] sm:rounded-[40px] bg-gradient-to-b from-white/10 to-white/0 border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.8)] backdrop-blur-xl w-full">
                  {/* Inner White Canvas (matches video background perfectly) */}
                  <div className="relative rounded-[24px] sm:rounded-[32px] overflow-hidden bg-[#FFFFFF] shadow-[inset_0_2px_10px_rgba(0,0,0,0.05)]">
                    <video 
                      src="/drishti-robo-feature.mp4"
                      preload="auto"
                      autoPlay
                      loop
                      muted
                      playsInline
                      controls={false}
                      className="w-full h-auto select-none block scale-[1.02]"
                    />
                  </div>
                </div>

                {/* 1. Analyze Chart Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", damping: 20, stiffness: 100, delay: 0.6 }}
                  className="absolute top-[18%] -right-4 sm:-right-10 md:-right-14 pointer-events-auto z-20"
                >
                  <motion.div
                    animate={{ y: [0, -8, 0], x: [0, 2, 0] }}
                    transition={{ repeat: Infinity, duration: 5.0, ease: "easeInOut" }}
                    whileHover={{ scale: 1.05, rotate: 1 }}
                    className="bg-gradient-to-br from-white/95 to-white/80 border border-white/70 ring-1 ring-black/5 backdrop-blur-[20px] shadow-[inset_0_2.5px_4px_rgba(255,255,255,0.8),0_12px_32px_-4px_rgba(0,132,255,0.12)] px-5 py-3 rounded-[20px] flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#0084FF] to-[#0066CC] flex items-center justify-center shadow-[0_4px_12px_rgba(0,132,255,0.3)] shrink-0">
                      <PenLine className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col text-left leading-tight">
                      <span className="font-sans font-black text-[13px] text-neutral-900 tracking-tight">Analyze Chart</span>
                      <span className="font-sans font-semibold text-[10px] text-neutral-500 mt-0.5">for strong demand</span>
                    </div>
                  </motion.div>
                </motion.div>

                {/* 2. Detect Zone Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", damping: 20, stiffness: 100, delay: 0.8 }}
                  className="absolute top-[48%] -left-6 sm:-left-12 md:-left-16 pointer-events-auto z-20"
                >
                  <motion.div
                    animate={{ y: [0, 8, 0], x: [0, -2, 0] }}
                    transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut" }}
                    whileHover={{ scale: 1.05, rotate: -1 }}
                    className="bg-gradient-to-br from-white/95 to-white/80 border border-white/70 ring-1 ring-black/5 backdrop-blur-[20px] shadow-[inset_0_2.5px_4px_rgba(255,255,255,0.8),0_12px_32px_-4px_rgba(16,185,129,0.12)] px-5 py-3 rounded-[20px] flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center shadow-[0_4px_12px_rgba(16,185,129,0.3)] shrink-0">
                      <FileText className="w-4 h-4 text-white" />
                    </div>
                    <div className="flex flex-col text-left leading-tight">
                      <span className="font-sans font-black text-[13px] text-neutral-900 tracking-tight">Detect Zone</span>
                      <span className="font-sans font-semibold text-[10px] text-neutral-500 mt-0.5">in real-time</span>
                    </div>
                  </motion.div>
                </motion.div>

                {/* 3. Track Setup Badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", damping: 20, stiffness: 100, delay: 1.0 }}
                  className="absolute bottom-[18%] -right-4 sm:-right-8 md:-right-12 pointer-events-auto z-20"
                >
                  <motion.div
                    animate={{ y: [0, -10, 0], x: [0, -1, 0] }}
                    transition={{ repeat: Infinity, duration: 4.8, ease: "easeInOut" }}
                    whileHover={{ scale: 1.05, rotate: 1.5 }}
                    className="bg-gradient-to-br from-white/95 to-white/80 border border-white/70 ring-1 ring-black/5 backdrop-blur-[20px] shadow-[inset_0_2.5px_4px_rgba(255,255,255,0.8),0_12px_32px_-4px_rgba(147,51,234,0.12)] px-5 py-3 rounded-[20px] flex items-center gap-3 cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#9333EA] to-[#7E22CE] flex items-center justify-center shadow-[0_4px_12px_rgba(147,51,234,0.3)] shrink-0">
                      <Check className="w-4 h-4 text-white stroke-[3px]" />
                    </div>
                    <div className="flex flex-col text-left leading-tight">
                      <span className="font-sans font-black text-[13px] text-neutral-900 tracking-tight">Track Setup</span>
                      <span className="font-sans font-semibold text-[10px] text-neutral-500 mt-0.5">for MIT entry</span>
                    </div>
                  </motion.div>
                </motion.div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══ HIGH-CONVERTING 2-OPTION SECTION ═══ */}
      <section id="plans" className="py-20 px-4 relative overflow-hidden border-t border-white/[0.04]">
        <div className="max-w-5xl mx-auto space-y-10">
          <div className="text-center space-y-3">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#00FF88]/10 border border-[#00FF88]/30">
              <Zap className="w-3.5 h-3.5 text-[#00FF88]" />
              <span className="text-[11px] font-black font-jetbrains text-[#00FF88] uppercase tracking-wider">
                TRANSPARENT INSTITUTIONAL ACCESS
              </span>
            </div>
            <h2 className="text-3xl sm:text-5xl font-black font-outfit">
              Get Started with <span className="bg-gradient-to-r from-[#00FF88] to-[#00F0FF] bg-clip-text text-transparent">Drishti AI</span>
            </h2>
            <p className="text-sm font-plex text-[#94A3B8] max-w-xl mx-auto">
              Only two simple choices: Start your 3-month free trial with instant AutoPay verification, or request direct institutional access from Admin Deepak.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {/* Option 1 */}
            <div className="md:col-span-7 rounded-3xl p-6 sm:p-8 bg-[#0B0D13] border border-[#00FF88] shadow-[0_0_40px_rgba(0,255,136,0.15)] flex flex-col justify-between">
              <div className="space-y-4">
                <span className="text-[10px] font-black font-jetbrains px-3 py-1 rounded-full uppercase bg-[#00FF88] text-black w-fit block">
                  ⭐ RECOMMENDED · 100% AUTOMATED
                </span>
                <h3 className="text-2xl font-black font-outfit text-white">
                  3 Months Free Trial
                </h3>
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl font-black font-jetbrains text-[#00FF88]">₹1</span>
                  <span className="text-sm line-through text-gray-500">₹3,000 value</span>
                  <span className="text-xs font-bold text-gray-300">to verify AutoPay</span>
                </div>
                <div className="p-4 rounded-2xl bg-black/50 border border-white/[0.06] text-xs font-plex space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-400">Today:</span>
                    <span className="font-bold text-[#00FF88]">₹1 (Refundable Verification)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Next 90 Days:</span>
                    <span className="font-bold text-[#00F0FF]">₹0 (100% Free Full Access)</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">After 90 Days:</span>
                    <span className="font-bold text-amber-400">₹1,000 / month (AutoPay)</span>
                  </div>
                </div>
                <div className="space-y-2 text-xs font-plex text-gray-300">
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" /> Real-time 15m & 1h Demand / Supply zones</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" /> Exact Entry, Stop Loss & dual Target levels</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" /> 1-Click Dhan Terminal & Risk Calculator integration</p>
                  <p className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-[#00FF88]" /> Zero lock-in: Cancel anytime in 1 click</p>
                </div>
              </div>

              <div className="pt-6 mt-6 border-t border-white/[0.08]">
                <Link to="/drishti" className="block">
                  <Button className="w-full h-12 bg-[#00FF88] hover:bg-[#00FF88]/90 !text-black font-black font-outfit text-sm rounded-xl shadow-[0_0_25px_rgba(0,255,136,0.3)]">
                    Pay ₹1 & Start 3 Months Free Trial <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Option 2 */}
            <div className="md:col-span-5 rounded-3xl p-6 sm:p-8 bg-[#0B0D13]/70 border border-white/[0.08] flex flex-col justify-between">
              <div className="space-y-4">
                <span className="text-[10px] font-black font-jetbrains px-3 py-1 rounded-full uppercase bg-white/10 text-zinc-300 w-fit block">
                  OFFLINE & INSTITUTIONAL
                </span>
                <h3 className="text-2xl font-bold font-outfit text-white">
                  Grant Access from Admin
                </h3>
                <p className="text-xs font-plex text-[#94A3B8] leading-relaxed">
                  Already enrolled offline in Jaipur or prefer paying through direct UPI / RTGS? Founder Deepak Sethia can grant instant access to your account.
                </p>
                <div className="p-4 rounded-2xl bg-black/30 border border-white/[0.05] text-xs font-plex space-y-2 text-gray-300">
                  <p>1. Submit request with your user email</p>
                  <p>2. Admin Deepak reviews enrollment</p>
                  <p>3. Instant unlock without page reload</p>
                </div>
              </div>

              <div className="space-y-3 pt-6 mt-6 border-t border-white/[0.08]">
                <Link to="/drishti" className="block">
                  <Button variant="outline" className="w-full h-11 border-white/10 text-white hover:bg-white/10 font-bold font-outfit text-xs rounded-xl">
                    Request Access from Admin
                  </Button>
                </Link>
                <a href="https://wa.me/919257059361?text=Hello%20Deepak%20Sir,%20I%20would%20like%20to%20get%20access%20to%20Drishti%20AI." target="_blank" rel="noopener noreferrer" className="block">
                  <Button className="w-full h-11 bg-[#25D366] hover:bg-[#25D366]/90 !text-white font-bold font-outfit text-xs rounded-xl shadow-xs">
                    WhatsApp Deepak Sir (+91 9257059361)
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/[0.04] py-8 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded overflow-hidden">
              <img src="/jamadhan-logo.png" alt="Jamadhan" className="w-full h-full object-cover" />
            </div>
            <span className="text-white font-bold font-outfit tracking-tight">JAMADHAN</span>
          </Link>
          <p className="text-xs text-[#52525B] font-plex">Drishti AI - See the Market Before It Moves</p>
        </div>
      </footer>
    </div>
  );
};

export default DrishtiLandingPage;
